"use client";

import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import Title from "@/core/common/Title";
import { IPackageDetailsData } from "@/app/package/[slug]/interface/IPackageDetails.interface";

interface PackageDescriptionProps {
  topTours: IPackageDetailsData[];
}

export default function PackageDescription({
  topTours: exclusiveHolidayData,
}: PackageDescriptionProps) {
  const [index, setIndex] = useState(0);

  const nextStep = useCallback(() => {
    setIndex((prev) => (prev + 1) % exclusiveHolidayData.length);
  }, [exclusiveHolidayData.length]);

  const prevStep = useCallback(() => {
    setIndex(
      (prev) =>
        (prev - 1 + exclusiveHolidayData.length) % exclusiveHolidayData.length
    );
  }, [exclusiveHolidayData.length]);

  useEffect(() => {
    const interval = setInterval(nextStep, 8000);
    return () => clearInterval(interval);
  }, [nextStep]);

  const getCardStyles = (itemIndex: number) => {
    const total = exclusiveHolidayData.length;
    let diff = itemIndex - index;

    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;

    const absDiff = Math.abs(diff);
    const isActive = diff === 0;

    return {
      x: diff * 280,
      scale: Math.max(1 - absDiff * 0.15, 0.7),
      opacity: absDiff > 2 ? 0 : 1 - absDiff * 0.3,
      zIndex: 10 - absDiff,
      rotateY: diff * -10,
      filter: isActive ? "grayscale(0%)" : "grayscale(60%)",
    };
  };

  return (
    <div className="w-full bg-slate-50 py-20 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl">
        <div className="absolute top-1/4 left-10 w-72 h-72 bg-primary-200/20 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-10 w-72 h-72 bg-blue-200/20 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4">
        <Title
          label="Top Curated Trips"
          primaryText="Discover Our"
          highlightText="Exclusive Tours"
        />

        <div className="relative mt-16 h-[550px] flex items-center justify-center perspective-[1200px]">
          {exclusiveHolidayData.map((item, i) => {
            const styles = getCardStyles(i);
            const isCenter = i === index;

            return (
              <motion.div
                key={item.id}
                initial={false}
                animate={styles}
                transition={{
                  type: "spring",
                  stiffness: 180,
                  damping: 25,
                }}
                className="absolute w-[320px] md:w-[400px] h-[480px] cursor-pointer"
                onClick={() => setIndex(i)}
              >
                <div
                  className={`relative w-full h-full rounded-[2.5rem] overflow-hidden shadow-2xl bg-white border-4 ${isCenter ? "border-white" : "border-transparent"}`}
                >
                  <Image
                    src={item.image || "/placeholder.png"}
                    alt={item.name}
                    fill
                    className="object-cover"
                    priority={isCenter}
                  />

                  <AnimatePresence>
                    {isCenter && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent flex flex-col justify-end p-8"
                      >
                        <motion.div
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.1 }}
                        >
                          <h3 className="text-2xl font-bold text-white mb-2">
                            {item.name}
                          </h3>
                          <p className="text-sm text-gray-200 line-clamp-2 mb-6 opacity-90">
                            {item.description?.replace(/<[^>]*>/g, "")}
                          </p>
                          <Link
                            href={`/package/${item.slug}`}
                            className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-full font-semibold hover:bg-primary-500 hover:text-white transition-all duration-300 group/btn"
                          >
                            View Details
                            <ArrowUpRight
                              size={18}
                              className="group-hover/btn:rotate-45 transition-transform"
                            />
                          </Link>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="flex justify-center gap-3 mt-12">
          {exclusiveHolidayData.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`h-1.5 transition-all duration-500 rounded-full ${
                index === i ? "w-12 bg-primary-500" : "w-3 bg-gray-300"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
